import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Copy, PhoneOutgoing, PhoneIncoming, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface ScriptCardProps {
  script: any; // Database script object
  onEdit: (script: any) => void;
  onDelete: (id: string) => void;
  onDuplicate: (script: any) => void;
}

export function ScriptCard({ script, onEdit, onDelete, onDuplicate }: ScriptCardProps) {
  const isOutbound = script.mode === 'outbound';
  
  return (
    <Card className={cn(
      "group hover:shadow-md transition-all duration-200 border-l-4",
      isOutbound 
        ? "border-l-primary hover:border-l-primary/80" 
        : "border-l-success hover:border-l-success/80"
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold">{script.name}</CardTitle>
            <div className="flex gap-2 mt-2">
              <Badge 
                variant={isOutbound ? 'default' : 'secondary'}
                className={cn(
                  "gap-1.5",
                  isOutbound 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-success text-success-foreground"
                )}
              >
                {isOutbound ? (
                  <PhoneOutgoing className="h-3 w-3" />
                ) : (
                  <PhoneIncoming className="h-3 w-3" />
                )}
                {isOutbound ? 'Outbound' : 'Inbound'}
              </Badge>
              {script.use_low_risk_mode && (
                <Badge variant="outline" className="gap-1 text-xs">
                  🕊️ Warm Intro Mode
                </Badge>
              )}
              
            </div>
          </div>
          {isOutbound ? (
            <PhoneOutgoing className="h-5 w-5 text-primary" />
          ) : (
            <PhoneIncoming className="h-5 w-5 text-success" />
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pb-3">
        <div className="space-y-2 text-sm text-muted-foreground">
          <div><span className="font-medium">Industry:</span> {script.target_industry || 'N/A'}</div>
          <div><span className="font-medium">Agent Name:</span> {script.user_name || 'No Agent Assigned'}</div>
          
          {script.prospects_count && (
            <div><span className="font-medium">Prospects:</span> {script.prospects_count}</div>
          )}
          {script.success_rate && (
            <div><span className="font-medium">Success Rate:</span> {script.success_rate}%</div>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="pt-3 border-t">
        <div className="flex items-center justify-between w-full">
          <div className="text-xs text-muted-foreground">
            Updated {format(new Date(script.updated_at), 'MMM d, yyyy')}
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button size="sm" variant="ghost" onClick={() => onDuplicate(script)}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onEdit(script)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => onDelete(script.id)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}